import type { ZoomLevel, MetricType, ChartDataPoint, FundingActivityData, AggregatedDataPoint } from './types';
import { CONFIG } from './constants';

export const currencyFormatter = (value: number, compact = false): string => {
    return compact
        ? new Intl.NumberFormat('en-US', { notation: 'compact', style: 'currency', currency: 'USD' }).format(value)
        : `$${value.toLocaleString()}`;
};

export const formatPeriodLabel = (date: Date, level: ZoomLevel): string => {
    const formatters = {
        year: () => date.getFullYear().toString(),
        month: () => date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        day: () => date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: '2-digit' }),
    };
    return formatters[level]();
};

export const getDateKey = (date: Date, level: ZoomLevel): string => {
    const keyGenerators = {
        year: () => date.getFullYear().toString(),
        month: () => `${date.getFullYear()}-${date.getMonth()}`,
        day: () => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`,
    };
    return keyGenerators[level]();
};

export const getPeriodStart = (date: Date, level: ZoomLevel): Date => {
    const periodStarts = {
        year: () => new Date(date.getFullYear(), 0, 1),
        month: () => new Date(date.getFullYear(), date.getMonth(), 1),
        day: () => new Date(date.getFullYear(), date.getMonth(), date.getDate()),
    };
    return periodStarts[level]();
};

export const generateDateRange = (startDate: Date, endDate: Date, level: ZoomLevel): Date[] => {
    const dates: Date[] = [];
    const current = new Date(startDate);

    const incrementors = {
        year: (date: Date) => date.setFullYear(date.getFullYear() + 1),
        month: (date: Date) => date.setMonth(date.getMonth() + 1),
        day: (date: Date) => date.setDate(date.getDate() + 1),
    };

    while (current <= endDate) {
        dates.push(new Date(current));
        incrementors[level](current);
    }

    return dates;
};

export const calculateTickValues = (chartData: ChartDataPoint[], metric: MetricType): number[] => {
    const hasNoData = chartData.length === 0 || chartData.every(item => item.value === 0);

    if (hasNoData) {
        return CONFIG.DEFAULT_TICKS[metric];
    }

    const maxValue = Math.max(...chartData.map(item => item.value));

    if (maxValue <= 4) {
        const ticks = [];
        for (let i = 0; i <= maxValue; i++) {
            ticks.push(i);
        }
        return ticks;
    }

    const step = maxValue / 4;
    const ticks = [0, Math.round(step), Math.round(step * 2), Math.round(step * 3), Math.round(maxValue)];
    return [...new Set(ticks)].sort((a, b) => a - b);
};

export const processChartData = (
    data: FundingActivityData[],
    zoomLevel: ZoomLevel
): AggregatedDataPoint[] => {
    if (data.length === 0) return [];

    const processedData = data
        .map(item => ({
            date: new Date(item.date),
            amount: item.amount,
            count: item.count,
        }))
        .sort((a, b) => a.date.getTime() - b.date.getTime());

    const minDate = processedData[0].date;
    const maxDate = processedData[processedData.length - 1].date;
    const startDate = getPeriodStart(minDate, zoomLevel);
    const endDate = getPeriodStart(maxDate, zoomLevel);
    const allDates = generateDateRange(startDate, endDate, zoomLevel);

    const dataMap = new Map<string, { amount: number; count: number }>();
    processedData.forEach(item => {
        const key = getDateKey(item.date, zoomLevel);
        if (!dataMap.has(key)) {
            dataMap.set(key, { amount: 0, count: 0 });
        }
        const group = dataMap.get(key)!;
        group.amount += item.amount;
        group.count += item.count;
    });

    return allDates.map(date => {
        const key = getDateKey(date, zoomLevel);
        const data = dataMap.get(key) || { amount: 0, count: 0 };
        return {
            label: formatPeriodLabel(date, zoomLevel),
            amount: data.amount,
            count: data.count,
            period: date,
        };
    });
};
